import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Link, useLocation } from "react-router-dom";
import type { StaffQuickAction } from "./quickActions.js";

export function QuickActionsMenu({
  actions,
  floating = false,
  onNavigate,
}: {
  actions: StaffQuickAction[];
  floating?: boolean;
  onNavigate?: (destination: string) => void;
}) {
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusOnOpen = useRef<"first" | "last" | null>(null);
  const menuId = useId();

  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    if (actions.length === 0) {
      setOpen(false);
      setActiveIndex(0);
      return;
    }
    setActiveIndex((current) => Math.min(current, actions.length - 1));
  }, [actions.length]);

  useEffect(() => {
    if (!open) return;

    function dismissOutside(event: PointerEvent) {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", dismissOutside);
    return () => document.removeEventListener("pointerdown", dismissOutside);
  }, [open]);

  useEffect(() => {
    if (!open || focusOnOpen.current === null) return;
    const items = menuItems();
    const target = focusOnOpen.current === "first" ? items[0] : items.at(-1);
    focusOnOpen.current = null;
    target?.focus();
  }, [open]);

  function menuItems(): HTMLAnchorElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLAnchorElement>('a[role="menuitem"]') ?? []);
  }

  function closeAndRestoreFocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function openAndFocus(edge: "first" | "last") {
    const index = edge === "first" ? 0 : Math.max(0, actions.length - 1);
    setActiveIndex(index);
    focusOnOpen.current = edge;
    setOpen(true);
  }

  function focusItem(index: number) {
    const items = menuItems();
    const target = items[index];
    if (!target) return;
    setActiveIndex(index);
    target.focus();
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      openAndFocus("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openAndFocus("last");
    }
  }

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      // This menu sits inside the mobile navigation drawer. Consume Escape here
      // so one keypress closes the nested menu first instead of also reaching
      // Sidebar's drawer-level Escape handler and moving focus into hidden UI.
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const items = menuItems();
    if (items.length === 0) return;
    event.preventDefault();

    if (event.key === "Home") {
      focusItem(0);
      return;
    }
    if (event.key === "End") {
      focusItem(items.length - 1);
      return;
    }

    const current = items.findIndex((item) => item === document.activeElement);
    const offset = event.key === "ArrowDown" ? 1 : -1;
    const next = current < 0 ? 0 : (current + offset + items.length) % items.length;
    focusItem(next);
  }

  function handleWidgetBlur(event: ReactFocusEvent<HTMLDivElement>) {
    if (event.relatedTarget instanceof Node && rootRef.current?.contains(event.relatedTarget)) return;
    setOpen(false);
  }

  if (actions.length === 0) return null;

  return (
    <div className={`staff-quick-actions${floating ? " staff-quick-actions--floating" : ""}`} ref={rootRef} onBlur={handleWidgetBlur}>
      <button
        ref={triggerRef}
        className="staff-new-trigger"
        type="button"
        aria-label={floating ? "Quick actions" : undefined}
        data-bottom-obstruction={floating ? "dock" : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) setOpen(false);
          else openAndFocus("first");
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        {floating ? (
          <svg aria-hidden="true" focusable="false" className="staff-new-plus" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        ) : (
          <span aria-hidden="true" className="staff-new-plus">+</span>
        )}
        {!floating && <span>New</span>}
        {!floating && <span aria-hidden="true" className="staff-new-chevron">⌄</span>}
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          className="staff-new-menu"
          role="menu"
          aria-label="Quick actions"
          onKeyDown={handleMenuKeyDown}
        >
          {actions.map((action, index) => (
            <Link
              key={action.id}
              className="staff-new-menu-item"
              role="menuitem"
              tabIndex={index === activeIndex ? 0 : -1}
              to={action.to}
              onFocus={() => setActiveIndex(index)}
              onClick={(event) => {
                setOpen(false);
                if (!event.defaultPrevented && event.button === 0 &&
                    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                  onNavigate?.(action.to);
                }
              }}
            >
              {action.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
